import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from '@remix-run/react';
import { useAtom, useAtomValue } from 'jotai';
import {
    uiStateAtomPageViewCount,
    PAGE_VIEWS_BEFORE_LOGIN_PROMPT,
    LOGIN_PROMPT_DELAY_MS,
    isCategoryPage,
} from '~/modules-rocco/root/states/ui-state-page-view-count';
import { uiStateAtomLoginModalOpened } from '~/modules-rocco/root/states/ui-state-login-modal-opened';
import { uiStateAtomAddProfessionalModalOpened } from '~/modules-rocco/root/states/ui-state-add-professional-modal-opened';
import { useUserLoggedIn } from '~/modules/user/hook/use-user';
import { useSetLoginModal } from '~/modules-rocco/user/hook/use-open-login-modal';

const LOGIN_PROMPT_TITLE =
    'Create a free account to save ideas, get quotes, and connect with professionals';

/**
 * Prompts logged-out users to sign in every N page navigations.
 *
 * Rules:
 * - A 10-second delay is enforced after every navigation before the prompt can appear.
 * - On category pages the user must also have started scrolling.
 * - The prompt is suppressed while another modal is already open.
 */
export const useLoginPromptOnPageView = () => {
    const { isLoggedIn } = useUserLoggedIn();
    const location = useLocation();
    const [pageViewCount, setPageViewCount] = useAtom(uiStateAtomPageViewCount);
    const { setLoginModal } = useSetLoginModal();

    const loginModalOpen = useAtomValue(uiStateAtomLoginModalOpened);
    const addProfessionalModalOpen = useAtomValue(
        uiStateAtomAddProfessionalModalOpened,
    );

    const previousPathname = useRef(location.pathname);

    const [promptPending, setPromptPending] = useState(false);
    const [hasScrolled, setHasScrolled] = useState(false);
    const [navigationTimestamp, setNavigationTimestamp] = useState(Date.now());

    const isCategory = isCategoryPage(location.pathname);

    /**
     * Ref that always reflects whether any site-wide modal is open so
     * the setTimeout callback can read the latest value.
     */
    const isAnyModalOpenRef = useRef(false);
    useEffect(() => {
        isAnyModalOpenRef.current = loginModalOpen || addProfessionalModalOpen;
    }, [loginModalOpen, addProfessionalModalOpen]);

    const openPrompt = useCallback(() => {
        setPromptPending(false);
        setLoginModal({
            authSource: 'siteGate',
            isOpen: true,
            title: LOGIN_PROMPT_TITLE,
        });
    }, [setLoginModal]);

    // --- 1. Count page views and flag when threshold is reached -----------
    useEffect(() => {
        if (isLoggedIn) return;
        if (location.pathname === previousPathname.current) return;

        previousPathname.current = location.pathname;
        setNavigationTimestamp(Date.now());
        setHasScrolled(false);

        const newCount = pageViewCount + 1;
        setPageViewCount(newCount);

        if (newCount % PAGE_VIEWS_BEFORE_LOGIN_PROMPT === 0) {
            setPromptPending(true);
        }
    }, [location.pathname, isLoggedIn, pageViewCount, setPageViewCount]);

    // --- 2. Track scrolling on category pages ----------------------------
    useEffect(() => {
        if (!isCategory || hasScrolled || isLoggedIn) return;

        const onScroll = () => setHasScrolled(true);
        window.addEventListener('scroll', onScroll, {
            once: true,
            passive: true,
        });
        return () => window.removeEventListener('scroll', onScroll);
    }, [isCategory, hasScrolled, isLoggedIn, location.pathname]);

    // --- 3. Show the prompt after delay + conditions are met --------------
    useEffect(() => {
        if (!promptPending || isLoggedIn) return;
        if (isCategory && !hasScrolled) return;
        if (loginModalOpen || addProfessionalModalOpen) return;

        const elapsed = Date.now() - navigationTimestamp;
        const remaining = Math.max(0, LOGIN_PROMPT_DELAY_MS - elapsed);

        const timer = setTimeout(() => {
            if (isAnyModalOpenRef.current) return;
            openPrompt();
        }, remaining);

        return () => clearTimeout(timer);
    }, [
        promptPending,
        isLoggedIn,
        isCategory,
        hasScrolled,
        loginModalOpen,
        addProfessionalModalOpen,
        openPrompt,
        navigationTimestamp,
    ]);
};
